import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';

import { STATISTIC_CONST } from '@/constant';
import {
  useTaskFrameStateQuery,
  useTaskObjectQuery,
  useTaskPeriodMetricQuery,
} from '@/hook';
import {
  CenterFocusWeak,
  Groups,
  History,
  Info,
  VFlex,
  VStack,
  VTab,
  VTabTrigger,
  VTabTriggerContainer,
  VideoLabel,
} from '@/package';
import type { FrameStateStatistic, Task, User } from '@/type';

import {
  calculateExtendClassData,
  calculateExtendClassDataWithBaseClass,
  calculatePeriodData,
  calculateTotalClassData,
  calculateTotalFrame,
  calculateTotalObject,
  calculateUserCount,
  filterUnknownClass,
} from '../Statistic';
import { SelectablePieChart } from '../Statistic';
import BarChart from '../Statistic/BarChart/BarChart';
import { DoughnutChart } from '../Statistic/DoughnutChart';
import { RangePicker } from '../Statistic/RangePicker';
import { StaticPieChart } from '../Statistic/StaticPieChart';
import { StatisticTable } from '../Statistic/StatisticTable';
import type { PeriodUnit } from '../Statistic/type';

export const TaskStatistics = ({
  taskDetail,
  selectedMember,
}: {
  taskDetail: Task;
  selectedMember?: User['id'];
}) => {
  return (
    <VStack
      gap={1.5}
      className="scrollbar-hide h-full w-[972px] overflow-x-hidden pt-4"
    >
      <TaskStatisticOverview
        taskDetail={taskDetail}
        selectedMember={selectedMember}
      />
      <TaskTotalLabeledObject
        taskDetail={taskDetail}
        selectedMember={selectedMember}
      />
      <TaskPeriodLabeledObject
        taskDetail={taskDetail}
        selectedMember={selectedMember}
      />
    </VStack>
  );
};

const TaskStatisticOverview = ({
  taskDetail: task,
  selectedMember,
}: {
  taskDetail: Task;
  selectedMember?: User['id'];
}) => {
  const { data: taskState } = useTaskFrameStateQuery(
    task.projectID,
    task.id,
    selectedMember,
  );
  const { data: taskObject } = useTaskObjectQuery(
    task.projectID,
    task.id,
    selectedMember,
    true,
  );

  const totalObject = calculateTotalObject(taskObject?.statExtendClass);

  const frameProgress = (taskState as FrameStateStatistic) ?? {
    prework: 0,
    working: 0,
    reject: 0,
    review: 0,
    reviewing: 0,
    done: 0,
  };
  const totalFrame = calculateTotalFrame(frameProgress);
  const totalUser = calculateUserCount(
    task?.userTask,
    (cur: { user?: { role?: string } }) => cur.user?.role,
  );

  return (
    <VStack gap={1.5} className="w-full">
      <div className="text-headline-md font-bold text-blue-300">Overview</div>
      <div className="grid h-[190px] grid-cols-2 gap-[14px]">
        <VStack className="h-full w-full" gap={1}>
          <VFlex
            gap={0.5}
            className="h-[87px] w-full rounded-md px-4 py-2 shadow-sm"
            items="center"
          >
            <Groups className="size-8" />

            <VStack>
              <span className="text-body-lg text-gray-600">Members</span>
              <VFlex
                className="text-body-md text-gray-300"
                items="center"
                gap={0.25}
              >
                <span>Manager</span>
                <span className="text-body-lg font-semibold text-black">
                  {Intl.NumberFormat().format(totalUser.manager)}
                </span>
                <div className="h-4 w-px bg-gray-300" />
                <span>Labeler</span>
                <span className="text-body-lg font-semibold text-black">
                  {Intl.NumberFormat().format(totalUser.worker)}
                </span>
              </VFlex>
            </VStack>
          </VFlex>

          <VFlex className="h-[87px] w-full" gap={1}>
            <VFlex
              gap={0.5}
              className="w-full flex-1 rounded-md px-4 py-2 shadow-sm"
              items="center"
            >
              <VideoLabel className="size-8" />

              <VStack>
                <span className="text-body-lg text-gray-600">Work Frames</span>
                <span className="text-body-lg font-semibold text-black">
                  {totalFrame}
                </span>
              </VStack>
            </VFlex>

            <VFlex
              gap={0.5}
              className="w-full flex-1 rounded-md px-4 py-2 shadow-sm"
              items="center"
            >
              <CenterFocusWeak className="size-8" />

              <VStack>
                <span className="text-body-lg text-gray-600">
                  Total Objects
                </span>
                <span className="text-body-lg font-semibold text-black">
                  {totalObject}
                </span>
              </VStack>
            </VFlex>
          </VFlex>
        </VStack>
        <VStack gap={0.5} className="rounded-md px-4 py-2 shadow-sm">
          <VFlex className="w-full justify-between">
            <VFlex className="items-center">
              <History className="size-5" />
              Total frames progress
            </VFlex>

            <div className="text-body-sm text-gray-600">Total {totalFrame}</div>
          </VFlex>
          <VFlex className="w-full justify-center">
            <DoughnutChart
              totalTask={totalFrame}
              taskProgress={{
                none: 0,
                pending: 0,
                ready: frameProgress.prework,
                progress:
                  frameProgress.working +
                  frameProgress.reviewing +
                  frameProgress.review +
                  frameProgress.reject,
                completed: frameProgress.done,
                fail: 0,
              }}
            />
          </VFlex>
        </VStack>
      </div>
    </VStack>
  );
};

const TaskTotalLabeledObject = ({
  taskDetail,
  selectedMember,
}: {
  taskDetail: Task;
  selectedMember?: User['id'];
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const { data: taskObject } = useTaskObjectQuery(
    taskDetail.projectID,
    taskDetail.id,
    selectedMember,
    true,
  );

  const superClassList =
    taskDetail?.extendClass?.filter(
      item => filterUnknownClass(item) && item.classType !== 'Extend',
    ) ?? [];

  const taskTotalData = calculateTotalClassData(
    superClassList,
    taskObject?.statExtendClass,
  );

  const extendClassData =
    typeof selectedIndex === 'number' && superClassList[selectedIndex]
      ? calculateExtendClassData(
          superClassList[selectedIndex]?.extendClass,
          taskObject?.statExtendClass,
        )
      : [];

  const extendClassDataWithBaseClass =
    typeof selectedIndex === 'number' && taskTotalData[selectedIndex]
      ? calculateExtendClassDataWithBaseClass(
          taskTotalData[selectedIndex],
          extendClassData,
        )
      : [];

  const toggleSelect = (index: number) => {
    setSelectedIndex(index === selectedIndex ? null : index);
  };

  return (
    <VStack gap={0.5} className="w-full">
      <VFlex className="w-full" justify="between" items="center">
        <div className="text-headline-md font-bold text-secondary-500">
          Total labeled objects
        </div>
        <VFlex items="center" justify="end" gap={0.25} className="flex-1">
          <Info className="size-4 text-State-info" />
          <div className="text-body-s">
            Clicking a pie chart slice or table item in the base class also
            allows you to view statistics for its extension classes.
          </div>
        </VFlex>
      </VFlex>
      <VFlex gap={2} items="center" justify="center">
        <SelectablePieChart
          pieData={taskTotalData}
          selectedValue={selectedIndex}
          onSelect={toggleSelect}
        />
        <StatisticTable
          data={taskTotalData}
          selectedValue={selectedIndex}
          onRowClick={toggleSelect}
        />
      </VFlex>

      {typeof selectedIndex === 'number' &&
        extendClassData.length > 0 &&
        taskTotalData[selectedIndex] && (
          <VFlex gap={2} items="center" justify="center">
            <StaticPieChart
              pieData={extendClassDataWithBaseClass}
              onClose={() => setSelectedIndex(null)}
            />
            <StatisticTable data={extendClassDataWithBaseClass} />
          </VFlex>
        )}
    </VStack>
  );
};

const TaskPeriodLabeledObject = ({
  taskDetail,
  selectedMember,
}: {
  taskDetail: Task;
  selectedMember?: User['id'];
}) => {
  const [periodUnit, setPeriodUnit] = useState<PeriodUnit>('day');
  const [between, setBetween] = useState<[Dayjs, Dayjs]>([
    dayjs(STATISTIC_CONST.TODAY).subtract(12, 'week'),
    dayjs(STATISTIC_CONST.TODAY),
  ]);
  const { data: taskMetric } = useTaskPeriodMetricQuery({
    projectID: taskDetail.projectID,
    taskID: taskDetail.id,
    period: periodUnit,
    between,
    workerID: selectedMember,
  });

  const extendClassList = taskMetric?.extendClass?.filter(
    item => item.classType !== 'Unknown',
  );

  const periodData = calculatePeriodData(
    extendClassList,
    taskMetric?.userMetric,
  );

  return (
    <VStack gap={0.5} className="h-full pb-[100px]">
      <div className="text-headline-md font-bold text-secondary-500">
        Labeled objects statistics
      </div>

      <VFlex className="w-full" justify="between" items="center">
        <div className="text-headline-s font-semibold text-primary-600">
          Trend
        </div>
        <VFlex items="center" className="w-full" justify="end" gap={0.5}>
          <RangePicker
            value={between}
            onChange={setBetween}
            periodUnit={periodUnit}
            key={periodUnit}
          />

          <VTab
            defaultValue="day"
            onValueChange={val => setPeriodUnit(val as PeriodUnit)}
            size="small"
            value={periodUnit}
            variant="secondary"
          >
            <VTabTriggerContainer>
              <VTabTrigger value="day">Daily</VTabTrigger>
              <VTabTrigger value="week">Weekly</VTabTrigger>
              <VTabTrigger value="month">Monthly</VTabTrigger>
            </VTabTriggerContainer>
          </VTab>
        </VFlex>
      </VFlex>

      {periodData.length > 0 && (
        <BarChart data={periodData} periodUnit={periodUnit} between={between} />
      )}
    </VStack>
  );
};
